// The Library: every source photo and result the caller has stored
// through their own account, browsable as a grid grouped by trial or by
// day (docs/plan/phase-10-ui.md "Library"). Read-only except for delete
// -- uploading happens in Generate, a specific run's results live in Task
// Detail; this is the account-wide contact sheet across every run and
// every direct upload.
//
// Thumbnails and video previews load straight from Store's download URL
// with the read token in the query string (api/store.ts's storeSrc,
// ADR-0019) rather than through a script fetch: that is what makes
// `loading="lazy"` and native `<video>` Range-seeking work for a grid
// that can hold hundreds of tiles -- fetching every tile's bytes into a
// blob up front, the way the Task Detail gallery does for its eager
// previews, would defeat both.
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ApiError, deleteArtifact, listArtifacts, type ArtifactListItem } from "../api/client";
import { storeSrc } from "../api/store";
import Drawer, { panelPageClass } from "../components/Drawer";
import { useFlip } from "../lib/flip";
import { compareHref } from "../lib/compare";
import { formatWhen } from "../lib/format";
import { downloadFileName, extensionFor, formatBytes, shareLink } from "../lib/results";
import { readStill, storeStill } from "../lib/stillCache";
import {
  DEFAULT_LIBRARY_FILTERS,
  contentTypePrefixFor,
  fileNameFor,
  filtersFromSearchParams,
  flattenGroups,
  formatDayHeading,
  groupLibraryItems,
  mediaDetailRows,
  mediaKindOf,
  searchParamsFromFilters,
  summarizeGroup,
  summaryLine,
  tileAltText,
  tileMetaLabel,
  visibleItems,
  type LibraryFilters,
  type RoleFilter,
  type TypeFilter,
} from "../lib/library";

const PAGE_SIZE = 60;
const QUERY_DEBOUNCE_MS = 300;
const SKELETON_TILES = 12;
// A video tile's cached still is a grid thumbnail, not the media: ~320 px
// wide at the video's aspect ratio, JPEG, is enough and keeps the data URL
// small enough to live in localStorage.
const STILL_WIDTH = 320;
const STILL_QUALITY = 0.6;
// Where in the clip to seek before capturing. A paused <video> at t=0 has
// no presented frame for drawImage (measured: a pure black image), so the
// still is taken from the frame a small seek presents, never from t=0.
const STILL_SEEK_S = 0.4;
// A capture whose `seeked` never arrives gives up after this and leaves
// the tile showing its live video.
const STILL_CAPTURE_TIMEOUT_MS = 2500;
// Luma standard deviation (0..255) below which a captured frame counts as
// unpaintable -- a uniform image, so it is discarded rather than cached.
const STILL_MIN_STDDEV = 2;
const STILL_SAMPLE_PX = 16;

/** Luma standard deviation of a canvas, sampled through a small downscaled
 * copy. A frame that was never presented comes back uniformly black, so a
 * deviation below STILL_MIN_STDDEV means the canvas holds no paintable
 * content. Undefined when the sample canvas cannot be read. */
function lumaStdDev(canvas: HTMLCanvasElement): number | undefined {
  const sample = document.createElement("canvas");
  sample.width = STILL_SAMPLE_PX;
  sample.height = STILL_SAMPLE_PX;
  const ctx = sample.getContext("2d");
  if (!ctx) return undefined;
  ctx.drawImage(canvas, 0, 0, STILL_SAMPLE_PX, STILL_SAMPLE_PX);
  const { data } = ctx.getImageData(0, 0, STILL_SAMPLE_PX, STILL_SAMPLE_PX);
  const luma = (r: number, g: number, b: number) => 0.2126 * r + 0.7152 * g + 0.0722 * b;
  const count = data.length / 4;
  let sum = 0;
  for (let i = 0; i < data.length; i += 4) sum += luma(data[i], data[i + 1], data[i + 2]);
  const mean = sum / count;
  let squared = 0;
  for (let i = 0; i < data.length; i += 4) {
    const d = luma(data[i], data[i + 1], data[i + 2]) - mean;
    squared += d * d;
  }
  return Math.sqrt(squared / count);
}

export default function Library() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const filters = useMemo(() => filtersFromSearchParams(searchParams), [searchParams]);

  // The search box is typed into immediately; what actually drives the
  // request (and the URL) only catches up QUERY_DEBOUNCE_MS after typing
  // stops.
  const [qInput, setQInput] = useState(filters.q);
  useEffect(() => setQInput(filters.q), [filters.q]);

  const [items, setItems] = useState<ArtifactListItem[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const closeDrawer = useCallback(() => setOpenId(null), []);
  // The grid's tiles glide to their new places when the panel opens or closes.
  const pageRef = useRef<HTMLDivElement>(null);
  // The artifact chosen as side A while the user picks side B. Comparing is
  // a two-click gesture -- "Compare with…" in a video's drawer, then a
  // second video tile -- rather than a form, because the two things being
  // compared are both already on screen.
  const [compareBase, setCompareBase] = useState<ArtifactListItem | null>(null);

  const updateFilters = useCallback(
    (patch: Partial<LibraryFilters>) => {
      setSearchParams(searchParamsFromFilters({ ...filters, ...patch }), { replace: true });
    },
    [filters, setSearchParams],
  );

  useEffect(() => {
    if (qInput === filters.q) return;
    const t = window.setTimeout(() => updateFilters({ q: qInput }), QUERY_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [qInput, filters.q, updateFilters]);

  const requestParams = useMemo(
    () => ({
      content_type_prefix: contentTypePrefixFor(filters.type),
      q: filters.q || undefined,
      trial: filters.trial || undefined,
    }),
    [filters.type, filters.q, filters.trial],
  );

  const load = useCallback(
    async (cursorArg: string | undefined, reset: boolean) => {
      if (reset) setLoading(true);
      else setLoadingMore(true);
      setError(null);
      try {
        const resp = await listArtifacts({ limit: PAGE_SIZE, cursor: cursorArg, ...requestParams });
        setItems((prev) => (reset ? resp.items : [...prev, ...resp.items]));
        setCursor(resp.next_cursor);
      } catch (e) {
        setError(e instanceof Error ? e.message : "failed to load the library");
      } finally {
        if (reset) setLoading(false);
        else setLoadingMore(false);
      }
    },
    [requestParams],
  );

  useEffect(() => {
    void load(undefined, true);
  }, [load]);

  const filtered = useMemo(
    () => visibleItems(items, filters.type, filters.role),
    [items, filters.type, filters.role],
  );
  const groups = useMemo(() => groupLibraryItems(filtered), [filtered]);
  const flat = useMemo(() => flattenGroups(groups), [groups]);
  const openIndex = openId ? flat.findIndex((it) => it.id === openId) : -1;
  const openItem = openIndex >= 0 ? flat[openIndex] : undefined;
  useFlip(pageRef, !!openItem);

  // Esc closes the viewer; left/right move through the tiles in the order
  // they are actually laid out on screen (flattenGroups), which is not
  // always the fetch order once trials interleave with ungrouped days.
  useEffect(() => {
    if (!openId) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpenId(null);
      } else if (e.key === "ArrowRight" && openIndex >= 0 && openIndex < flat.length - 1) {
        setOpenId(flat[openIndex + 1].id);
      } else if (e.key === "ArrowLeft" && openIndex > 0) {
        setOpenId(flat[openIndex - 1].id);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openId, openIndex, flat]);

  // Esc leaves the pick as well as closing the drawer, so a comparison that
  // was started by mistake never traps the grid in pick mode.
  useEffect(() => {
    if (!compareBase) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setCompareBase(null);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [compareBase]);

  /** A tile click: opens the drawer, unless a side A is waiting for its
   * partner -- then a *different* video opens the comparator instead.
   * Anything else (the same tile, an image) still opens the drawer, so a
   * pick can never dead-end on a click that was not the one being asked
   * for. */
  function onTileOpen(item: ArtifactListItem) {
    if (
      compareBase &&
      compareBase.id !== item.id &&
      mediaKindOf(compareBase) === "video" &&
      mediaKindOf(item) === "video"
    ) {
      setCompareBase(null);
      navigate(compareHref({ a: compareBase.id, b: item.id }), {
        state: { a: compareBase, b: item },
      });
      return;
    }
    setOpenId(item.id);
  }

  function onRetry() {
    void load(undefined, true);
  }

  function onLoadMore() {
    if (cursor && !loadingMore) void load(cursor, false);
  }

  async function onCopyLink(item: ArtifactListItem) {
    setActionError(null);
    const link = shareLink(item);
    if (!link) {
      setActionError("no link yet for " + item.id.slice(0, 8) + ": the file is still being committed.");
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(item.id);
      window.setTimeout(() => setCopiedId((cur) => (cur === item.id ? null : cur)), 1500);
    } catch {
      // Clipboard access needs a secure context and a user gesture; when
      // it is refused, show the link so it can still be copied by hand.
      setActionError("could not copy to the clipboard. The link is: " + link);
    }
  }

  async function onDelete(item: ArtifactListItem) {
    if (!confirm("Delete this file? This can't be undone.")) return;
    setActionError(null);
    setDeletingId(item.id);
    try {
      await deleteArtifact(item.id);
      setItems((prev) => prev.filter((it) => it.id !== item.id));
      setOpenId((cur) => (cur === item.id ? null : cur));
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setActionError("Used by active work — can't delete yet.");
      } else {
        setActionError(e instanceof Error ? e.message : "failed to delete");
      }
    } finally {
      setDeletingId(null);
    }
  }

  const dlHref = openItem ? storeSrc(openItem) : undefined;

  return (
    <div className={panelPageClass("u-page u-lib-page", !!openItem)} ref={pageRef}>
      <div className="u-head">
        <h1 className="u-h1">Library</h1>
        <p className="u-lede">Every source photo and result you have stored, in one place.</p>
      </div>

      <div className="u-sheet__bar u-lib-filters">
        <label className="u-field u-field--row">
          Type
          <select
            className="u-select"
            value={filters.type}
            onChange={(e) => updateFilters({ type: e.target.value as TypeFilter })}
          >
            <option value="all">All</option>
            <option value="image">Images</option>
            <option value="video">Videos</option>
            <option value="other">Other</option>
          </select>
        </label>
        <label className="u-field u-field--row">
          Show
          <select
            className="u-select"
            value={filters.role}
            onChange={(e) => updateFilters({ role: e.target.value as RoleFilter })}
          >
            <option value="all">All</option>
            <option value="source">Sources</option>
            <option value="result">Results</option>
          </select>
        </label>
        <label className="u-field u-field--row u-lib-search">
          Search
          <input
            className="u-input"
            type="search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="label contains…"
          />
        </label>
        {filters.trial && (
          <span className="u-lib-chip">
            <span className="u-lib-chip__label">trial: {filters.trial}</span>
            <button
              type="button"
              className="u-lib-chip__x"
              onClick={() => updateFilters({ trial: undefined })}
              aria-label="Clear trial filter"
            >
              ×
            </button>
          </span>
        )}
      </div>

      {error ? (
        <p className="u-error">
          {error}{" "}
          <button className="u-btn u-btn--quiet" onClick={onRetry}>
            Retry
          </button>
        </p>
      ) : loading ? (
        <ul className="u-grid u-grid--wide" aria-hidden="true" data-testid="library-skeleton">
          {Array.from({ length: SKELETON_TILES }).map((_, i) => (
            <li key={i} className="u-frame u-lib-tile u-lib-tile--skeleton">
              <div className="u-frame__stage" />
            </li>
          ))}
        </ul>
      ) : items.length === 0 ? (
        <div className="u-empty">
          <p>Nothing in your Library yet.</p>
          <p className="u-note">
            Source photos and results land here as soon as you upload them: start a run from{" "}
            <Link to="/">Generate</Link>, or check a run's results in <Link to="/tasks">Tasks</Link>.
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="u-empty">
          <p>No files match these filters.</p>
          <button className="u-btn u-btn--quiet" onClick={() => updateFilters(DEFAULT_LIBRARY_FILTERS)}>
            Clear filters
          </button>
        </div>
      ) : (
        <div className="u-lib-groups" data-testid="library-groups">
          {groups.map((group) => {
            const summary = summarizeGroup(group.items);
            return (
              <section className="u-lib-group" key={group.key}>
                {group.kind === "trial" ? (
                  <button
                    type="button"
                    className="u-lib-group__head u-lib-group__head--clickable"
                    onClick={() => updateFilters({ trial: group.trial })}
                    title="Filter the Library to this trial"
                  >
                    <span className="u-lib-group__title">{group.trial}</span>
                    <span className="u-note">{summaryLine(summary)}</span>
                  </button>
                ) : (
                  <div className="u-lib-group__head">
                    <span className="u-lib-group__title">{formatDayHeading(group.day as string)}</span>
                    <span className="u-note">{summaryLine(summary)}</span>
                  </div>
                )}
                <ul className="u-grid u-grid--wide">
                  {group.items.map((item) => (
                    <LibraryTile
                      key={item.id}
                      item={item}
                      picking={!!compareBase && compareBase.id !== item.id}
                      onOpen={() => onTileOpen(item)}
                    />
                  ))}
                </ul>
              </section>
            );
          })}

          {cursor && (
            <div className="u-lib-more">
              <button className="u-btn" disabled={loadingMore} onClick={onLoadMore}>
                {loadingMore ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </div>
      )}

      {compareBase && (
        <p className="u-lib-picking" role="status">
          Comparing with <strong>{compareBase.label || compareBase.id.slice(0, 8)}</strong> — click a
          second video tile to see them on one playhead.{" "}
          <button className="u-btn u-btn--quiet" onClick={() => setCompareBase(null)}>
            Cancel
          </button>
        </p>
      )}

      {actionError && <p className="u-error">{actionError}</p>}

      <Drawer
        open={!!openItem}
        title={openItem ? openItem.label || openItem.id.slice(0, 8) : ""}
        label={openItem?.label || "file details"}
        onClose={closeDrawer}
      >
        {openItem && (
          <>
          <div className="u-drawer__media">
            {mediaKindOf(openItem) === "video" && dlHref ? (
              <video src={dlHref} controls preload="metadata" />
            ) : mediaKindOf(openItem) === "image" && dlHref ? (
              <img src={dlHref} alt={tileAltText(openItem)} />
            ) : (
              <p className="u-note" style={{ padding: 18 }}>
                {extensionFor(openItem.content_type).toUpperCase()} file — no preview.
              </p>
            )}
          </div>
          <dl className="u-drawer__meta">
            <dt>Label</dt>
            <dd>{openItem.label || "—"}</dd>
            <dt>Type</dt>
            <dd>{openItem.content_type || "—"}</dd>
            <dt>Size</dt>
            <dd>{openItem.size ? formatBytes(openItem.size) : "—"}</dd>
            <dt>Created</dt>
            <dd>{formatWhen(openItem.created_at)}</dd>
            {mediaDetailRows(openItem.media).map((row) => (
              <Fragment key={"media-" + row.label}>
                <dt>{row.label}</dt>
                <dd>{row.value}</dd>
              </Fragment>
            ))}
            {Object.entries(openItem.attrs).map(([k, v]) => (
              <Fragment key={k}>
                <dt>{k}</dt>
                <dd>{String(v)}</dd>
              </Fragment>
            ))}
          </dl>
          <div className="u-drawer__acts">
            {dlHref ? (
              <a className="u-btn" href={dlHref} download={downloadFileName(fileNameFor(openItem))}>
                Download
              </a>
            ) : (
              <button className="u-btn" disabled>
                Download
              </button>
            )}
            {mediaKindOf(openItem) === "video" && (
              <button
                className="u-btn"
                onClick={() => {
                  setOpenId(null);
                  setCompareBase(openItem);
                }}
              >
                Compare with…
              </button>
            )}
            {mediaKindOf(openItem) === "video" && (
              <Link className="u-btn" to={`/library/cut/${openItem.id}`}>
                Cut…
              </Link>
            )}
            <button className="u-btn" onClick={() => void onCopyLink(openItem)}>
              {copiedId === openItem.id ? "Copied" : "Copy link"}
            </button>
            <button
              className="u-btn u-btn--red"
              disabled={deletingId === openItem.id}
              onClick={() => void onDelete(openItem)}
            >
              {deletingId === openItem.id ? "Deleting…" : "Delete"}
            </button>
          </div>
          </>
        )}
      </Drawer>
    </div>
  );
}

/** A grid tile: an image thumbnail, a video preview, or a plain file
 * marker, all behind one open-the-viewer surface (a div, not a button, so
 * a video tile's play/pause control -- a real button -- can sit inside it
 * without nesting one button inside another). */
function LibraryTile({
  item,
  onOpen,
  picking,
}: {
  item: ArtifactListItem;
  onOpen: () => void;
  /** Set while the grid is waiting for the second side of a comparison:
   * a video tile that would be a valid partner is marked as one. */
  picking?: boolean;
}) {
  const kind = mediaKindOf(item);
  const src = storeSrc(item);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const isVideoTile = kind === "video" && !!src;

  // The artifact's still, captured on an earlier visit to this browser.
  // Read synchronously so the first render already carries it: the tile
  // paints from the cached still with no media request, while the video's
  // own metadata load continues in the background exactly as before.
  const [poster, setPoster] = useState<string | undefined>(() =>
    isVideoTile ? readStill(item.id) : undefined,
  );

  // At most one capture in flight per tile; holding its teardown is what
  // marks the capture as pending. Cleared on completion, on the timeout,
  // and on unmount.
  const pendingCapture = useRef<(() => void) | null>(null);
  useEffect(() => () => pendingCapture.current?.(), []);

  // Capture a decoded frame as a small JPEG and cache it for the next
  // visit. An artifact's bytes never change after commit, so the still
  // never goes stale. The frame has to be *presented* first: a paused
  // <video> at t=0 has nothing for drawImage and yields pure black
  // (measured), so the element is seeked a little way in and the capture
  // happens on the `seeked` that follows. Best-effort throughout: a
  // tainted canvas (toDataURL throws), an unpaintable frame, or a
  // full/disabled storage skips caching rather than breaking the tile.
  function beginCapture(v: HTMLVideoElement) {
    if (poster || pendingCapture.current) return;
    if (!v.videoWidth || !v.videoHeight) return;
    const duration = v.duration;
    const offset =
      Number.isFinite(duration) && duration > 0 ? Math.min(STILL_SEEK_S, duration / 10) : STILL_SEEK_S / 4;
    const onSeeked = () => {
      endCapture();
      captureFrame(v);
    };
    function endCapture() {
      window.clearTimeout(timer);
      v.removeEventListener("seeked", onSeeked);
      if (pendingCapture.current === endCapture) pendingCapture.current = null;
    }
    const timer = window.setTimeout(endCapture, STILL_CAPTURE_TIMEOUT_MS);
    pendingCapture.current = endCapture;
    v.addEventListener("seeked", onSeeked);
    v.currentTime = offset;
  }

  function captureFrame(v: HTMLVideoElement) {
    try {
      const canvas = document.createElement("canvas");
      canvas.width = Math.min(STILL_WIDTH, v.videoWidth);
      canvas.height = Math.round(canvas.width * (v.videoHeight / v.videoWidth));
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
      // A uniform frame is not a picture: keep the live video and try
      // again on a later visit rather than caching a blank still.
      const stddev = lumaStdDev(canvas);
      if (stddev === undefined || stddev < STILL_MIN_STDDEV) return;
      const dataUrl = canvas.toDataURL("image/jpeg", STILL_QUALITY);
      storeStill(item.id, dataUrl);
      setPoster(dataUrl);
    } catch {
      /* no still this visit; the video shows its own first frame as before */
    }
  }

  function play() {
    videoRef.current?.play().catch(() => undefined);
    setPlaying(true);
  }
  function stop() {
    const v = videoRef.current;
    if (v) {
      v.pause();
      v.currentTime = 0;
    }
    setPlaying(false);
  }

  return (
    <li className={"u-frame u-lib-tile" + (picking ? " u-lib-tile--pick" : "")} data-flip={item.id}>
      <div
        className="u-frame__stage"
        role="button"
        tabIndex={0}
        aria-label={`Open ${tileAltText(item)}`}
        onClick={onOpen}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen();
          }
        }}
        // Hover or keyboard focus on the tile plays a video preview on a
        // desktop pointer; a touch screen never fires either, which is
        // what the always-visible play/pause button below is for.
        onMouseEnter={isVideoTile ? play : undefined}
        onMouseLeave={isVideoTile ? stop : undefined}
        onFocus={isVideoTile ? play : undefined}
        onBlur={isVideoTile ? stop : undefined}
      >
        {kind === "image" && src ? (
          <img src={src} alt={tileAltText(item)} loading="lazy" />
        ) : isVideoTile ? (
          <>
            {/* Chromium honours loading="lazy" on <video> the same way it does on <img>:
                a tile that is not near the viewport fires no Store request at all. React's
                types do not carry the attribute for media elements, hence the spread.
                `poster` carries the locally cached still (lib/stillCache.ts): present from
                the first render on a revisit, so the grid paints instantly while the
                metadata request below still runs exactly as it always did. */}
            <video
              ref={videoRef}
              src={src}
              poster={poster}
              muted
              playsInline
              preload="metadata"
              loop
              onLoadedData={(e) => beginCapture(e.currentTarget)}
              {...{ loading: "lazy" } as Record<string, unknown>}
              aria-label={tileAltText(item)}
            />
            <button
              type="button"
              className="u-lib-video__play"
              aria-pressed={playing}
              aria-label={playing ? "Pause preview" : "Play preview"}
              onClick={(e) => {
                e.stopPropagation();
                if (playing) stop();
                else play();
              }}
            >
              {playing ? "❚❚" : "►"}
            </button>
          </>
        ) : (
          <span className="u-lib-file">
            <span className="u-lib-file__ext">{extensionFor(item.content_type)}</span>
            <span className="u-lib-file__size">{item.size ? formatBytes(item.size) : "—"}</span>
          </span>
        )}
      </div>
      <div className="u-frame__cap">
        <span className="u-frame__name">{item.label || item.id.slice(0, 8)}</span>
        <span className="u-frame__meta">{tileMetaLabel(item)}</span>
      </div>
    </li>
  );
}
